/**
 * The actor layer's authoring rules — D232 (`docs/design/actors.md` §2, §7;
 * `actors-content.md` §5.2).
 *
 * @remarks
 * An actor is a new entity kind (Mike's ruling 1), and every rule here is
 * the reference walk or the D31/D47 dead-content law applied to it:
 *
 * - Actor ids are unique across the pack, and never an item's or a room's:
 *   the three families share one id space (D11), and a topic key with an
 *   underscore is read against all three.
 * - `initialRoomId` and every `audibleFrom` room are declared rooms, and
 *   `audibleFrom` lists each once.
 * - **An actor that is never on stage is dead content** (D233 relaxed round
 *   A's rule from "no `initialRoomId`" to "no `initialRoomId` AND no
 *   `move_actor` carrying it onto a room"; D235 relaxed it once more: a body
 *   HEARD from somewhere — `audibleFrom` — is on stage by ear, a voice with a
 *   location that is not the player's, never present and addressable from
 *   those rooms). The field is optional so a thing
 *   can arrive later without a flag pretending it was always there — the
 *   drain animal is offstage until the gas — but a body nothing ever places
 *   or hears has every line unreachable.
 * - **`arrivalNarratives` need an arrival, `departureNarratives` a
 *   departure** (D233; the D47 family's rule — lines that could never speak
 *   are refused). An arrival is a `move_actor` onto a room; a departure
 *   needs the body to stand somewhere a move can take it away from — a
 *   starting room and any move, or two moves to different places.
 * - **`blocksExits` names exits of rooms the body can stand in** (D233;
 *   `actors.md` §7) — a body cannot stand in the way of a door in a room it
 *   never reaches. Each must be a declared exit of its `initialRoomId` or
 *   of a `move_actor` destination.
 * - **A follower's declaration names real things** (D238;
 *   `actor-follows-validation.ts`): its `while`, every refusal's rooms,
 *   `when` and lines, and its rejoin lines.
 * - `speakerId` names a declared speaker, and a speaker is at most one
 *   actor's voice: an embodied speaker's lines carry its name as the body's
 *   signature (Calder's §6), and two bodies under one name would lie about
 *   who spoke.
 * - `verbNarratives` keys name a declared verb by its canonical token (an
 *   alias would never match — the engine reads the map with the canonical
 *   token), and never a verb an actor ANSWERS (`ask`, `inspect`, `look`),
 *   which never reach the refusal the lines re-voice. Unlike an item's
 *   `unsupportedVerbNarratives`, a mechanized verb is a legal key: every
 *   item verb refuses on a body, so `take` is exactly where "She would
 *   notice" belongs. Judged against the MERGED vocabulary, like D47's rule,
 *   so it waits for the compile.
 * - `visibleWhen`, the description's readings, the fragments, every
 *   narratives field and (D251) every authored verb's arms walk like an
 *   item's — the ids they name must exist.
 *   Topics are the topic layer's (`topic-validation.ts` reads actors too),
 *   and a `source` naming no speaker is the speaker layer's structural walk.
 *
 * @packageDocumentation
 */

import type { CompiledLexicon } from '@orpheus/parser-engine';

import {
	type ActorDefinition,
	type ContentPackDefinition,
	roomsAnActorCanStandIn,
	type StageDirections,
	stageDirectionsOf,
	verbInteractionArmsOf,
} from '@orpheus/content-schema';

import { checkFollows } from './actor-follows-validation.js';
import { brokenReference, collectDuplicates } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import {
	checkCondition,
	checkDescriptionFragments,
	checkNarrativeLines,
	checkNarrativeText,
	type DeclaredIdSets,
	declaredIdSetsOf,
} from './reference-walkers.js';

/** The verbs an actor ANSWERS rather than refuses — never a `verbNarratives` key. */
const VERBS_AN_ACTOR_ANSWERS: readonly string[] = ['ask', 'inspect', 'look'];

/**
 * Validate every actor's declaration against the pack — the step-3
 * cross-checks, before the vocabulary compiles.
 *
 * @param contentPack - The parsed pack
 * @returns Every failure found
 */
export const validateActorAuthoring = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];
	const declared = declaredIdSetsOf(contentPack);

	collectDuplicates(
		contentPack.actors.map((actor, actorIndex) => ({ declarationPath: `actors[${String(actorIndex)}].actorId`, declaredId: actor.actorId })),
		'actor',
		failures,
	);

	const speakerIds = new Set(contentPack.speakers.map((speaker) => speaker.speakerId as string));
	const actorIndexBySpeaker = new Map<string, number>();
	// D233: every move in the pack, read once — where each body can ever
	// stand; D238: with the player's routes, which carry a follower.
	const stage = stageDirectionsOf(contentPack);
	const exitIdsByRoomId = new Map(contentPack.rooms.map((room) => [room.roomId as string, new Set(room.exits.map((exit) => exit.exitId as string))]));

	contentPack.actors.forEach((actor, actorIndex) => {
		const actorPath = `actors[${String(actorIndex)}]`;

		// One id space (D11): a body named like a thing or a place would make
		// every id-shaped reference — a topic key, a condition — ambiguous.
		if (declared.itemIds.has(actor.actorId) || declared.roomIds.has(actor.actorId)) {
			failures.push(brokenReference(`${actorPath}.actorId`, actor.actorId, 'an actor shares its id with an item or a room — content ids are one space'));
		}

		checkPlacement(actor, actorPath, declared, stage, failures);
		checkMovementProse(actor, actorPath, stage, failures);
		checkBlockedExits(actor, actorPath, declared, stage, exitIdsByRoomId, failures);
		checkFollows(actor, actorPath, { declared, entryRoomId: contentPack.manifest.entryRoomId, stage }, failures);
		checkVoice(actor, actorPath, actorIndex, speakerIds, actorIndexBySpeaker, failures);
		checkProse(actor, actorPath, declared, failures);
	});

	return failures;
};

/**
 * Where the actor is, and where it is heard from: declared rooms, each
 * `audibleFrom` once, and SOME way onto the stage — a starting room, or a
 * move onto one.
 *
 * @param actor - The actor
 * @param actorPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param stage - The pack's stage directions
 * @param failures - Accumulator
 */
const checkPlacement = (
	actor: ActorDefinition,
	actorPath: string,
	declared: DeclaredIdSets,
	stage: StageDirections,
	failures: ReferenceBrokenFailure[],
): void => {
	if (actor.initialRoomId === undefined) {
		if (roomsAnActorCanStandIn(actor, stage).length === 0 && actor.audibleFrom.length === 0) {
			failures.push(
				brokenReference(
					`${actorPath}.initialRoomId`,
					actor.actorId,
					'the actor declares no initialRoomId, no move_actor ever carries it onto a room, and no room hears it (audibleFrom) — it is never on stage, and every line it has is unreachable; give it a room, a move onto one, or rooms it is heard from',
				),
			);
		}
	} else if (!declared.roomIds.has(actor.initialRoomId)) {
		failures.push(brokenReference(`${actorPath}.initialRoomId`, actor.initialRoomId, 'the actor starts in an undeclared room'));
	}

	const seenRooms = new Set<string>();

	actor.audibleFrom.forEach((roomId, roomIndex) => {
		const roomPath = `${actorPath}.audibleFrom[${String(roomIndex)}]`;

		if (!declared.roomIds.has(roomId)) {
			failures.push(brokenReference(roomPath, roomId, 'the actor is heard from an undeclared room'));
		}

		if (seenRooms.has(roomId)) {
			failures.push(brokenReference(roomPath, roomId, 'audibleFrom lists the same room twice'));
		}

		seenRooms.add(roomId);
	});
};

/**
 * The lines a move speaks must have a move to speak them (D233): an arrival
 * needs a `move_actor` onto a room; a departure needs the body to stand
 * somewhere a move can take it away from.
 *
 * @param actor - The actor
 * @param actorPath - Its path, for the failures
 * @param stage - The pack's stage directions
 * @param failures - Accumulator
 */
const checkMovementProse = (actor: ActorDefinition, actorPath: string, stage: StageDirections, failures: ReferenceBrokenFailure[]): void => {
	const movesOfThisActor = stage.actorMoves.filter((move) => move.actorId === actor.actorId);

	if (actor.arrivalNarratives !== undefined && !movesOfThisActor.some((move) => move.destination !== 'offstage')) {
		failures.push(
			brokenReference(
				`${actorPath}.arrivalNarratives`,
				actor.actorId,
				'arrivalNarratives are spoken when a move_actor carries the actor into the player\'s room, and no move_actor carries it onto any room — the lines could never speak',
			),
		);
	}

	// A departure is witnessed from a room the body stood in; it can stand in
	// its starting room, in every move's destination and (D238) wherever it is
	// led, and a move whose destination differs from one of those is a
	// departure from it. A follow itself is silent, so it departs nowhere.
	const standsIn = roomsAnActorCanStandIn(actor, stage);
	const canDepart = standsIn.some((roomId) => movesOfThisActor.some((move) => move.destination !== roomId));

	if (actor.departureNarratives !== undefined && !canDepart) {
		failures.push(
			brokenReference(
				`${actorPath}.departureNarratives`,
				actor.actorId,
				'departureNarratives are spoken when a move_actor carries the actor out of the player\'s room, and no move_actor ever takes it away from a room it can stand in — the lines could never speak',
			),
		);
	}
};

/**
 * The exits a body stands in the way of are exits of rooms it can stand in
 * (D233; `actors.md` §7).
 *
 * @param actor - The actor
 * @param actorPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param stage - The pack's stage directions
 * @param exitIdsByRoomId - Each room's exits
 * @param failures - Accumulator
 */
const checkBlockedExits = (
	actor: ActorDefinition,
	actorPath: string,
	declared: DeclaredIdSets,
	stage: StageDirections,
	exitIdsByRoomId: ReadonlyMap<string, ReadonlySet<string>>,
	failures: ReferenceBrokenFailure[],
): void => {
	const standsIn = roomsAnActorCanStandIn(actor, stage);

	actor.blocksExits.forEach((exitId, exitIndex) => {
		const exitPath = `${actorPath}.blocksExits[${String(exitIndex)}]`;

		if (!declared.exitIds.has(exitId)) {
			failures.push(brokenReference(exitPath, exitId, 'the actor blocks an undeclared exit'));

			return;
		}

		if (!standsIn.some((roomId) => exitIdsByRoomId.get(roomId)?.has(exitId) === true)) {
			failures.push(
				brokenReference(
					exitPath,
					exitId,
					'the actor blocks an exit of a room it never stands in — it is placed in or moved to no room that has this exit, so it could never be in the way',
				),
			);
		}
	});
};

/**
 * The actor's voice: a declared speaker, and nobody else's.
 *
 * @param actor - The actor
 * @param actorPath - Its path, for the failures
 * @param actorIndex - Its index, recorded against its speaker
 * @param speakerIds - The pack's declared speaker ids
 * @param actorIndexBySpeaker - Which actor already claimed each speaker (grown here)
 * @param failures - Accumulator
 */
const checkVoice = (
	actor: ActorDefinition,
	actorPath: string,
	actorIndex: number,
	speakerIds: ReadonlySet<string>,
	actorIndexBySpeaker: Map<string, number>,
	failures: ReferenceBrokenFailure[],
): void => {
	if (actor.speakerId === undefined) {
		return;
	}

	if (!speakerIds.has(actor.speakerId)) {
		failures.push(brokenReference(`${actorPath}.speakerId`, actor.speakerId, 'the actor speaks as a speaker the pack does not declare (D192: declare it under `speakers`)'));

		return;
	}

	const claimedBy = actorIndexBySpeaker.get(actor.speakerId);

	if (claimedBy === undefined) {
		actorIndexBySpeaker.set(actor.speakerId, actorIndex);
	} else {
		failures.push(
			brokenReference(
				`${actorPath}.speakerId`,
				actor.speakerId,
				`a speaker is one actor's voice — actors[${String(claimedBy)}] already speaks as it, and an embodied speaker's lines carry its name as that body's signature`,
			),
		);
	}
};

/**
 * Every id the actor's prose names — its gate, its page, its answers and
 * its refusals — must exist.
 *
 * @param actor - The actor
 * @param actorPath - Its path, for the failures
 * @param declared - The pack's declared-id universe
 * @param failures - Accumulator
 */
const checkProse = (actor: ActorDefinition, actorPath: string, declared: DeclaredIdSets, failures: ReferenceBrokenFailure[]): void => {
	if (actor.visibleWhen !== undefined) {
		checkCondition(actor.visibleWhen, `${actorPath}.visibleWhen`, declared, failures);
	}

	checkNarrativeText(actor.description, `${actorPath}.description`, declared, failures);
	checkDescriptionFragments(actor.descriptionFragments, `${actorPath}.descriptionFragments`, declared, failures);
	checkNarrativeLines(actor.arrivalNarratives, `${actorPath}.arrivalNarratives`, declared, failures);
	checkNarrativeLines(actor.askNarratives, `${actorPath}.askNarratives`, declared, failures);
	checkNarrativeLines(actor.blockingNarratives, `${actorPath}.blockingNarratives`, declared, failures);
	checkNarrativeLines(actor.departureNarratives, `${actorPath}.departureNarratives`, declared, failures);
	checkNarrativeLines(actor.greetingNarratives, `${actorPath}.greetingNarratives`, declared, failures);
	checkNarrativeLines(actor.unreachableNarratives, `${actorPath}.unreachableNarratives`, declared, failures);

	for (const [verbToken, lines] of Object.entries(actor.verbNarratives ?? {})) {
		checkNarrativeLines(lines, `${actorPath}.verbNarratives.${verbToken}`, declared, failures);
	}

	checkNarrativeLines(actor.orderFallbackNarratives, `${actorPath}.orderFallbackNarratives`, declared, failures);

	// D251/D252: a body's authored verbs and its orders read the world and speak as a thing's verbs do (their effects:
	// `checkEveryEffect`).
	for (const { arm, path } of [...verbInteractionArmsOf(actor.verbInteractions), ...verbInteractionArmsOf(actor.orders, 'orders')]) {
		arm.conditions.forEach((condition, conditionIndex) => {
			checkCondition(condition, `${actorPath}.${path}.conditions[${String(conditionIndex)}]`, declared, failures);
		});
		checkNarrativeLines(arm.narratives, `${actorPath}.${path}.narratives`, declared, failures);
	}
};

/**
 * Why a verb key could never be reached by the engine's canonical lookup — it names no declared verb, or an alias of
 * one — or `undefined` when it can be.
 *
 * @param verbToken - The key
 * @param lexicon - The compiled, merged vocabulary
 * @param neverWhat - What could never happen under an undeclared key ("these lines could never be spoken")
 * @returns The reason, or `undefined`
 */
const unreachableVerbKeyReason = (verbToken: string, lexicon: CompiledLexicon, neverWhat: string): string | undefined => {
	const intent = lexicon.intentsByToken.get(verbToken);

	if (intent === undefined) {
		return `no verb by that name is declared, so ${neverWhat}`;
	}

	return intent.canonicalToken === verbToken ? undefined : `"${verbToken}" is an alias of "${intent.canonicalToken}" — key on the canonical verb, which answers every alias`;
};

/**
 * Every actor's verb-keyed tables, judged against the merged vocabulary (the D47 pattern: `knock` reaches most packs
 * from the base, so this waits for the compile): its `verbNarratives` (D232) — declared, canonical, and never a verb
 * the actor answers — and (D252) its `orders` — declared and canonical, and any verb at all besides: the player may
 * tell a body to do what the player cannot, so an unsupported verb or the engine's own is a key like any other.
 *
 * @param contentPack - The parsed pack
 * @param lexicon - The compiled, merged vocabulary
 * @returns Every failure found
 */
export const validateActorVerbKeys = (contentPack: ContentPackDefinition, lexicon: CompiledLexicon): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	contentPack.actors.forEach((actor, actorIndex) => {
		const actorPath = `actors[${String(actorIndex)}]`;

		for (const verbToken of Object.keys(actor.verbNarratives ?? {})) {
			const reason =
				unreachableVerbKeyReason(verbToken, lexicon, 'these lines could never be spoken') ??
				(VERBS_AN_ACTOR_ANSWERS.includes(verbToken)
					? 'an actor answers that verb (looking reads its description; asking reads its topics and greeting), so it never reaches the refusal these lines re-voice'
					: undefined);

			if (reason !== undefined) {
				failures.push(brokenReference(`${actorPath}.verbNarratives.${verbToken}`, verbToken, reason));
			}
		}

		for (const verbToken of Object.keys(actor.orders ?? {})) {
			const reason = unreachableVerbKeyReason(verbToken, lexicon, 'this order could never be given');

			if (reason !== undefined) {
				failures.push(brokenReference(`${actorPath}.orders.${verbToken}`, verbToken, reason));
			}
		}
	});

	return failures;
};
